import { type InferArgs } from "./v.js";
import type { QueryDef, QueryHandler } from "./reactive-query-types.js";
import { defineRealtimeSubscriptions } from "./realtime-query-registry.js";
import type { RealtimeQuerySubscriptionInput } from "./realtime-query-registry.js";

// ─── query registry (globalThis) ─────────────────────────
// globalThis shared singleton — dual-bundle safe (see analysis-dual-module-registry.md).

declare global {
  var __gencow_queryRegistry: Map<string, QueryDef<any, any>>;
}

if (!globalThis.__gencow_queryRegistry) globalThis.__gencow_queryRegistry = new Map();

/**
 * @deprecated Internal legacy registry.
 * Use `procedure` from your app's `gencow/runtime.ts` instead.
 */
export const queryRegistry = globalThis.__gencow_queryRegistry;

/**
 * @deprecated Use `procedure.query` from your app's `gencow/runtime.ts` instead.
 */
export function query<TSchema = any, TReturn = any>(
  key: string,
  handlerOrDef:
    | QueryHandler<InferArgs<TSchema>, TReturn>
    | {
        args?: TSchema;
        public?: boolean;
        realtime?: RealtimeQuerySubscriptionInput<TSchema>;
        handler: QueryHandler<InferArgs<TSchema>, TReturn>;
      },
): QueryDef<TSchema, TReturn> {
  let handler: QueryHandler<InferArgs<TSchema>, TReturn>;
  let argsSchema: TSchema | undefined;
  let isPublic = false;
  let realtime: RealtimeQuerySubscriptionInput<TSchema> | undefined;

  if (typeof handlerOrDef === "function") {
    handler = handlerOrDef;
  } else {
    handler = handlerOrDef.handler;
    argsSchema = handlerOrDef.args;
    isPublic = handlerOrDef.public === true;
    realtime = handlerOrDef.realtime;
  }

  const def: QueryDef<TSchema, TReturn> = { key, handler, argsSchema, isPublic, realtime };
  queryRegistry.set(key, def);
  if (realtime) {
    const subscriptionInput = {
      [key]: {
        ...realtime,
        args: realtime.args ?? argsSchema,
      },
    } as Record<string, RealtimeQuerySubscriptionInput>;
    defineRealtimeSubscriptions(subscriptionInput);
  }
  return def;
}

/**
 * @deprecated Legacy query registry API.
 * Use `procedure` from your app's `gencow/runtime.ts` instead.
 */
export function getQueryHandler(key: string): QueryHandler | undefined {
  return queryRegistry.get(key)?.handler;
}

/**
 * @deprecated Legacy query registry API.
 * Use `procedure` from your app's `gencow/runtime.ts` instead.
 */
export function getQueryDef(key: string): QueryDef | undefined {
  return queryRegistry.get(key);
}

/**
 * @deprecated Legacy query registry API.
 * Use `procedure` from your app's `gencow/runtime.ts` instead.
 */
export function getRegisteredQueries(): string[] {
  return Array.from(queryRegistry.keys());
}
